'use client'

import { useEffect, useSyncExternalStore } from 'react'

import { getUser } from '@/app/actions/user'

import type { User } from '@/payload-types'

let requestId = 0
let latestUser: User | null | undefined = undefined
const listeners = new Set<() => void>()

function emit(user: User | null) {
  latestUser = user
  listeners.forEach((listener) => listener())
}

function subscribe(onStoreChange: () => void) {
  listeners.add(onStoreChange)
  return () => {
    listeners.delete(onStoreChange)
  }
}

function getSnapshot(): User | null | undefined {
  return latestUser
}

function getServerSnapshot(): undefined {
  return undefined
}

/**
 * Re-fetch the authenticated Payload user and notify every `useCurrentUser` subscriber.
 * Password / magic-link / email-verification in EventAuthModal stay on the same page,
 * so providers must refresh instead of trusting their first mount value.
 */
export async function refreshCurrentUser(): Promise<User | null> {
  const id = ++requestId
  const result = await getUser()
  if (id !== requestId) {
    return latestUser ?? null
  }
  const user = result.status === 'error' ? null : (result.data ?? null)
  emit(user)
  return user
}

/**
 * Fetch the currently authenticated Payload user client-side.
 * Returns `undefined` while loading and `null` when unauthenticated.
 */
export function useCurrentUser(): User | null | undefined {
  const user = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)

  useEffect(() => {
    if (latestUser === undefined) {
      void refreshCurrentUser()
    }
  }, [])

  return user
}
